import React from "react";

import { useTheme } from "@catchoftheday/theme";

import { Flex } from "../Flex";
import {
  IconAlert,
  IconProps,
  IconTick,
  IconToolTip,
  IconWarning,
} from "../Icon";

import { WrapperProps } from "./types";

type InlineAlertTheme = {
  color: string;
  iconColor: string;
  backgroundColor: string;
};

export const INLINE_ALERT_VARIANT_MAP: {
  [k: string]: {
    theme: {
      dark: InlineAlertTheme;
      light: InlineAlertTheme;
    };
    icon: React.FC<IconProps>;
  };
} = {
  error: {
    theme: {
      dark: {
        color: "white",
        iconColor: "white",
        backgroundColor: "error",
      },
      light: {
        color: "text",
        iconColor: "error",
        backgroundColor: "#FCE9E9",
      },
    },
    icon: IconAlert,
  },
  warning: {
    theme: {
      dark: {
        color: "text",
        iconColor: "text",
        backgroundColor: "warning",
      },
      light: {
        color: "text",
        iconColor: "warning",
        backgroundColor: "#FEF3DE",
      },
    },
    icon: IconWarning,
  },
  success: {
    theme: {
      dark: {
        color: "white",
        iconColor: "white",
        backgroundColor: "primary",
      },
      light: {
        color: "text",
        iconColor: "primary",
        backgroundColor: "#F0FAE6",
      },
    },
    icon: IconTick,
  },
  info: {
    theme: {
      dark: {
        color: "white",
        iconColor: "white",
        backgroundColor: "secondary",
      },
      light: {
        color: "text",
        iconColor: "secondary",
        backgroundColor: "#D0E8FF",
      },
    },
    icon: IconToolTip,
  },
  default: {
    theme: {
      dark: {
        color: "white",
        iconColor: "white",
        backgroundColor: "text",
      },
      light: {
        color: "white",
        iconColor: "white",
        backgroundColor: "text",
      },
    },
    icon: IconToolTip,
  },
};

export const InlineAlert: React.FC<WrapperProps> = ({
  variant,
  containerProps,
  showAlertIcon: _showAlertIcon,
  alignIcon = "center",
  icon: _icon,
  theme = "light",
  children,
}) => {
  const cgTheme = useTheme();
  const showAlertIcon = _showAlertIcon ?? Boolean(_icon);
  const AlertIcon = INLINE_ALERT_VARIANT_MAP[variant].icon;
  const iconColor = INLINE_ALERT_VARIANT_MAP[variant].theme[theme].iconColor;
  const color = INLINE_ALERT_VARIANT_MAP[variant].theme[theme].color;
  const backgroundColor =
    INLINE_ALERT_VARIANT_MAP[variant].theme[theme].backgroundColor;

  const icon = _icon
    ? React.cloneElement(_icon, {
        height: _icon.props.height ?? "20px",
        width: _icon.props.width ?? "20px",
      })
    : undefined;

  return (
    <Flex
      role="alert"
      width="auto"
      borderRadius={{ xs: "4px", lg: "none" }}
      color={color}
      backgroundColor={backgroundColor}
    >
      <Flex
        position="relative"
        marginX="auto"
        width="100%"
        maxWidth={cgTheme.maxWidths.cotdWebsite ?? cgTheme.maxWidths.default}
      >
        {showAlertIcon && (
          <Flex
            alignItems={alignIcon}
            py={{ xs: "8px", lg: "10px" }}
            pl="16px"
            color={iconColor}
          >
            {icon ?? <AlertIcon height="20px" width="20px" />}
          </Flex>
        )}
        <Flex
          alignItems="center"
          fontSize={{ xs: "xs", lg: "base" }}
          fontWeight="normal"
          py={{ xs: "10px", lg: "12px" }}
          pl="12px"
          pr="20px"
          {...containerProps}
        >
          {children}
        </Flex>
      </Flex>
    </Flex>
  );
};
